import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { useState } from 'react';
import { FiUpload, FiX } from 'react-icons/fi';
import './file-drop.css'; // Ensure you have the corresponding CSS file

export type FileDropSelectedFile = {
  clientId: string;
  name: string;
  status?: 'uploading' | 'uploaded' | 'error';
  error?: string;
};

type FileDropProps = {
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (clientId: string) => void;
  selectedFiles?: FileDropSelectedFile[];
  className?: {
    wrapper?: string;
  };
  accept?: string;
  showAcceptText?: boolean;
};

export const FileDrop = ({
  accept = 'image/png, image/jpeg, image/gif',
  showAcceptText = true,
  className,
  onFilesSelected,
  onFileRemove,
  selectedFiles = [],
}: FileDropProps) => {
  const { t } = useTranslations();
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    processFiles(droppedFiles);
  };

  const processFiles = (fileList: FileList) => {
    const fileArray = Array.from(fileList);
    onFilesSelected(fileArray);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const openFileDialog = () => {
    document.getElementById('fileInput')?.click();
  };

  const acceptExtensions = accept
    .split(',')
    .map(ext => ext.trim().split('/').pop())
    .join(', ')
    .toUpperCase();

  return (
    <div className={cn('', 'ea-file-drop', className?.wrapper)}>
      <input
        id="fileInput"
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }} // Hide the file input
        accept={accept}
      />
      <div className="file-list flex flex-col gap-2">
        {selectedFiles.map(file => (
          <div
            key={file.clientId}
            className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{file.name}</span>
              {file.status === 'uploading' && (
                <span className="text-xs text-muted-foreground">Nahrava sa...</span>
              )}
              {file.status === 'error' && (
                <span className="text-xs text-destructive">
                  {file.error || 'Subor sa nepodarilo nahrat.'}
                </span>
              )}
            </span>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              className="shrink-0 text-destructive hover:text-destructive"
              onClick={() => onFileRemove(file.clientId)}
              aria-label={t('text146')}
            >
              <FiX />
            </Button>
          </div>
        ))}
      </div>
      <div
        className={`file-drop flex flex-col items-center justify-center gap-2 rounded-lg py-5 transition-all duration-200 ${
          dragOver && 'border-primary bg-primary/5 py-6'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <FiUpload className={'h-9 w-9 text-primary'} />
        <div>
          {showAcceptText && <p className={cn('text-muted-foreground', '')}>{acceptExtensions}</p>}
          <p className={cn('text-muted-foreground', '')}>{t('text948')}</p>
        </div>
        <Button type="button" onClick={openFileDialog} variant={'default-alt'}>
          {t('text950')}
        </Button>
      </div>
    </div>
  );
};

export default FileDrop;
